import { z } from 'zod';
/**
 * Contrats du scanner d'indexation (T1-C, XPHOTO-01/-02/-03/-15).
 * Source unique de vérité front + back.
 *
 * - `scanRequestSchema` : corps de `POST /api/libraries/:id/scan`.
 * - `scanCountsSchema` : ventilation de RÉCONCILIATION disque ↔ index, exposée dans
 *   `jobs.result` (aperçu dry-run comme run réel) et lue par l'écran Admin. Elle
 *   rend l'écart « X fichiers sur disque ≠ Y médias en galerie » ENTIÈREMENT
 *   explicable (indexé + copies exactes fusionnées + ignorés par raison).
 */
/** Corps d'enfilement d'un scan (dry-run optionnel). */
export const scanRequestSchema = z.object({
    dryRun: z.boolean().optional(),
});
/**
 * RAISON d'exclusion d'un fichier VU sur le disque mais NON indexé. Rend l'écart
 * disque↔galerie explicable fichier par fichier :
 * - `unsupported_extension` : extension hors de l'allow-list média (ex. `.heic`,
 *                             `.cr2`, `.psd`, `.avif` non encore pris en charge).
 * - `non_media`             : fichier annexe / non média (sidecar `.xmp`, `.txt`,
 *                             `Thumbs.db`, `.db`, `.ini`, `.json`…), attendu.
 * - `unreadable`            : fichier illisible (accès disque, `ENOENT`/`EACCES`).
 * - `decode_error`          : décodage/traitement du média échoué (média corrompu,
 *                             vignette impossible, sonde/extraction ffmpeg…).
 * - `mismatched_type`       : l'extension annonce un type média mais le CONTENU
 *                             détecté (magic bytes / ExifTool `MIMEType`) est autre
 *                             (le fameux « PDF nommé .png »).
 */
export const scanSkipReasonSchema = z.enum([
    'unsupported_extension',
    'non_media',
    'unreadable',
    'decode_error',
    'mismatched_type',
]);
/** Liste ordonnée & exhaustive des raisons (itération UI / init des compteurs). */
export const SCAN_SKIP_REASONS = scanSkipReasonSchema.options;
/** Décompte des fichiers ignorés PAR RAISON (chaque clé ≥ 0). */
export const scanSkippedByReasonSchema = z.object({
    unsupported_extension: z.number().int().nonnegative(),
    non_media: z.number().int().nonnegative(),
    unreadable: z.number().int().nonnegative(),
    decode_error: z.number().int().nonnegative(),
    mismatched_type: z.number().int().nonnegative(),
});
/** Borne du nombre d'échantillons de chemins ignorés joints au résultat. */
export const SCAN_SKIPPED_SAMPLES_MAX = 100;
/**
 * Échantillon (borné) d'un fichier ignoré, pour l'inspection sans avoir à consulter
 * le journal complet. `detectedMime` = vrai type par le contenu (ou null si l'échec
 * a précédé toute lecture). `ext` = extension en minuscules (`` si aucune).
 */
export const scanSkippedSampleSchema = z.object({
    relPath: z.string(),
    ext: z.string(),
    detectedMime: z.string().nullable(),
    reason: scanSkipReasonSchema,
});
/**
 * Compteurs de réconciliation d'un scan. INVARIANT vérifiable :
 *   `filesWalked = indexed + exactCollapsed + skippedTotal`
 * où `indexed = created + moved + seen` et `skippedTotal = Σ skippedByReason`.
 *
 * - `total` / `filesWalked` : fichiers parcourus sur le disque. `total` (historique)
 *   = fichiers à EXTENSION MÉDIA reconnue ; `filesWalked` = TOUS les fichiers
 *   rencontrés (média ou non) → base de la réconciliation avec `find … | wc -l`.
 * - `created`       : nouvelles identités insérées.
 * - `moved`         : identités retrouvées à un nouveau `rel_path` (déplacement).
 * - `seen`          : identités revues au même `rel_path` (mise à jour volatile).
 * - `indexed`       : `created + moved + seen` (= médias visibles en galerie).
 * - `exactCollapsed`: copies pixel-identiques d'une identité déjà vue au scan
 *                     (non insérées ; unicité `unique(library_id, identity_hash)`) —
 *                     PLUSIEURS fichiers disque ↦ UN seul asset en galerie.
 * - `missing`       : assets de la bibliothèque non revus → `is_missing = true`.
 * - `skipped`       : fichiers ignorés (total, tous motifs) — rétrocompatible.
 * - `skippedByReason`: ventilation de `skipped` par motif (somme = `skipped`).
 * - `skippedSamples` : échantillon borné de chemins ignorés (débogage direct).
 * - `thumbnails`    : vignettes générées lors de ce run (0 en dry-run).
 */
export const scanCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    filesWalked: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    moved: z.number().int().nonnegative(),
    seen: z.number().int().nonnegative(),
    indexed: z.number().int().nonnegative(),
    exactCollapsed: z.number().int().nonnegative(),
    missing: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    skippedByReason: scanSkippedByReasonSchema,
    skippedSamples: z.array(scanSkippedSampleSchema).max(SCAN_SKIPPED_SAMPLES_MAX),
    thumbnails: z.number().int().nonnegative(),
});
/**
 * Réconciliation du DERNIER scan terminé (`GET /api/libraries/:id/scan-reconciliation`).
 * `counts` = le `ScanCounts` persisté dans `jobs.result` du dernier job `scan` `done`
 * (null si aucun scan n'a encore abouti). `finishedAt` = fin de ce scan (ISO/null).
 * `dryRun` distingue un aperçu d'un vrai run.
 */
export const scanReconciliationSchema = z.object({
    counts: scanCountsSchema.nullable(),
    finishedAt: z.string().datetime({ offset: true }).nullable(),
    dryRun: z.boolean(),
});
/**
 * Ventilation des EXTENSIONS refusées (raison `unsupported_extension`) du DERNIER
 * scan terminé, pour l'écran de réconciliation (`GET /reconciliation/extensions`).
 * Chaque entrée = une extension (en minuscules, `.` inclus, ou `` si aucune) et son
 * nombre d'occurrences. Ordonné par occurrence décroissante puis extension.
 */
export const reconciliationExtensionsResultSchema = z.object({
    extensions: z.array(z.object({
        ext: z.string(),
        count: z.number().int().nonnegative(),
    })),
});
/**
 * Motif d'export d'une liste TXT de fichiers ignorés (`GET /reconciliation/skipped-export`).
 * `all` = tous les ignorés ; `non_media` / `unsupported_extension` = ciblés par raison.
 * Aligné sur les codes d'erreur du scanner (`NON_MEDIA` / `UNSUPPORTED_EXTENSION`).
 */
export const reconciliationExportReasonSchema = z.enum([
    'all',
    'non_media',
    'unsupported_extension',
]);
//# sourceMappingURL=scan.js.map